import { useCallback, useRef } from 'preact/hooks';
import type { Asset } from '@schibsted-svp/svp-api-types';
import type { StreamProvider } from '@vgtv/api-client';
import {
  getAssetAccess,
  getAssetImage,
  getAssetPreviewUrl,
} from '../../../../utils/player/asset';
import { useTracker } from '../../hooks/useTracker';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';
import { getPortalHostname } from '../../../../config/helpers/getPortalHostname';
import { useProvider } from '../../hooks/useProvider';
import { IdleLayoutElements } from '../IdleLayout/IdleLayoutElements';
import { VideoPreview } from '../../components/VideoPreview/VideoPreview';
import { getImageSrc } from '../../utils/image';
import { usePlayerContainer } from '../../hooks/usePlayerContainer';
import { isHermesApp } from '../../../../utils/device';

export const shouldLinkToPortal = ({
  playbackState,
  asset,
  provider,
  hasAccess,
  hasError,
}: {
  playbackState: jwplayer.InternalPlayerState;
  asset?: Asset;
  provider: StreamProvider | 'brandstudio';
  hasAccess: boolean;
  hasError: boolean;
}) =>
  provider === 'vgtv' &&
  window.top !== window &&
  ['idle', 'error'].includes(playbackState) &&
  asset &&
  (getAssetAccess(asset) || hasError) &&
  !hasAccess;

export const PortalLinkLayout = () => {
  const playerContainer = usePlayerContainer();
  const layoutRef = useRef<HTMLAnchorElement>(null!);

  const { assetProvider } = useProvider();
  const { asset } = useCurrentAsset();
  const { trackButtonClick } = useTracker();

  const previewUrl = getAssetPreviewUrl(asset);
  const posterSrc = getImageSrc(
    asset && getAssetImage(asset, 'main'),
    playerContainer.clientWidth
  );

  const targetUrl = `https://${getPortalHostname(assetProvider)}/${asset?.id}?utm_source=embed`;

  const handleClick = useCallback(() => {
    if (!asset) {
      return;
    }

    trackButtonClick('portal-link', {
      '@id': `sdrn:vgtv:video:${asset.id}`,
      '@type': 'Video',
      'name': asset.title,
      'url': targetUrl,
    });
  }, [asset, trackButtonClick, targetUrl]);

  return (
    <a
      ref={layoutRef}
      href={targetUrl}
      rel="noopener"
      target={isHermesApp ? '_top' : '_blank'}
      className="vgp-idle-layout vgp-idle-layout-not-started"
      onClick={handleClick}
      style={{ backgroundImage: `url(${posterSrc})` }}
      role="button"
      tabIndex={0}
    >
      {previewUrl && <VideoPreview url={previewUrl} containerRef={layoutRef} />}
      <IdleLayoutElements asset={asset} />
    </a>
  );
};
